import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Send, ArrowLeft, Users } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { Linkify } from "@/components/Linkify";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { supabase } from "@/integrations/supabase/client";
import {
  getGroupBySlug,
  getMyGroupMembership,
  listGroupMessages,
  sendGroupMessage,
} from "@/lib/groups.functions";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/comunidades/$slug/chat")({
  head: () => ({ meta: [{ title: "Chat do grupo — Viny Club" }] }),
  component: GrupoChat,
});

function formatTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function formatDayLabel(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  const yest = new Date(); yest.setDate(today.getDate() - 1);
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(d, today)) return "Hoje";
  if (sameDay(d, yest)) return "Ontem";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "long" });
}

function GrupoChat() {
  const { slug } = Route.useParams();
  const { profile, isAuthenticated, loading } = useCurrentUser();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const getGroup = useServerFn(getGroupBySlug);
  const getMembership = useServerFn(getMyGroupMembership);
  const listMsgs = useServerFn(listGroupMessages);
  const sendFn = useServerFn(sendGroupMessage);

  const groupQ = useQuery({
    queryKey: ["group", slug],
    queryFn: () => getGroup({ data: { slug } }),
  });
  const groupId = groupQ.data?.id;

  const memQ = useQuery({
    queryKey: ["group-membership", groupId],
    queryFn: () => getMembership({ data: { groupId: groupId! } }),
    enabled: !!groupId && isAuthenticated,
  });

  const msgQ = useQuery({
    queryKey: ["group-messages", groupId],
    queryFn: () => listMsgs({ data: { groupId: groupId! } }),
    enabled: !!groupId && !!memQ.data,
  });

  useEffect(() => {
    if (!groupId) return;
    const ch = supabase
      .channel(`group-msgs:${groupId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "group_messages", filter: `group_id=eq.${groupId}` },
        () => qc.invalidateQueries({ queryKey: ["group-messages", groupId] }),
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [groupId, qc]);

  const [text, setText] = useState("");
  const taRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const send = useMutation({
    mutationFn: () => sendFn({ data: { groupId: groupId!, body: text } }),
    onSuccess: () => {
      setText("");
      if (taRef.current) taRef.current.style.height = "auto";
      qc.invalidateQueries({ queryKey: ["group-messages", groupId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [msgQ.data?.length]);

  // Auto-grow textarea
  const onInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    const el = e.target;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 140) + "px";
  };

  // Group messages into runs (same author + close in time) with day dividers
  const groups = useMemo(() => {
    const list = msgQ.data ?? [];
    const out: Array<
      | { kind: "day"; key: string; label: string }
      | { kind: "run"; key: string; userId: string; author: { username?: string | null; avatar_url?: string | null } | null; items: typeof list }
    > = [];
    let lastDay = "";
    let currentRun: any = null;
    for (const m of list) {
      const day = new Date(m.created_at).toDateString();
      if (day !== lastDay) {
        out.push({ kind: "day", key: `d-${day}`, label: formatDayLabel(m.created_at) });
        lastDay = day;
        currentRun = null;
      }
      const prev = currentRun?.items[currentRun.items.length - 1];
      const closeInTime = prev && new Date(m.created_at).getTime() - new Date(prev.created_at).getTime() < 5 * 60 * 1000;
      if (currentRun && currentRun.userId === m.user_id && closeInTime) {
        currentRun.items.push(m);
      } else {
        currentRun = {
          kind: "run",
          key: `r-${m.id}`,
          userId: m.user_id,
          author: m.author ?? null,
          items: [m],
        };
        out.push(currentRun);
      }
    }
    return out;
  }, [msgQ.data]);

  if (loading || groupQ.isLoading) {
    return <AppShell><div className="p-8 text-center text-sm text-muted-foreground">Carregando...</div></AppShell>;
  }
  if (!groupQ.data) {
    return <AppShell><div className="p-8 text-center text-sm text-muted-foreground">Grupo não encontrado</div></AppShell>;
  }
  if (!isAuthenticated) {
    return (
      <AppShell>
        <div className="mx-auto max-w-md px-4 py-20 text-center">
          <p className="mb-4 text-sm text-muted-foreground">Entre para acessar o chat do grupo</p>
          <button onClick={() => navigate({ to: "/auth" })} className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground">Entrar</button>
        </div>
      </AppShell>
    );
  }
  if (!memQ.data) {
    return (
      <AppShell>
        <div className="mx-auto max-w-md px-4 py-20 text-center">
          <p className="mb-4 text-sm text-muted-foreground">Você precisa ser membro deste grupo para conversar.</p>
          <Link to="/comunidades/$slug" params={{ slug }} className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground">Ir para o grupo</Link>
        </div>
      </AppShell>
    );
  }

  const group = groupQ.data;

  const submit = () => {
    const t = text.trim();
    if (!t || send.isPending) return;
    send.mutate();
  };

  return (
    <AppShell>
      {/* Full-screen chat surface. Mobile: dvh minus top+bottom nav. Desktop: dvh minus top nav. */}
      <div className="mx-auto flex h-[calc(100dvh-56px-72px-env(safe-area-inset-bottom))] w-full max-w-3xl flex-col bg-background lg:h-[calc(100dvh-72px)]">

        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-[color:var(--hairline)] bg-background/95 px-3 py-2.5 backdrop-blur supports-[backdrop-filter]:bg-background/70">
          <Link
            to="/comunidades/$slug"
            params={{ slug }}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full hover:bg-surface"
            aria-label="Voltar"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <Link
            to="/comunidades/$slug"
            params={{ slug }}
            className="flex min-w-0 flex-1 items-center gap-2.5"
          >
            {group.avatar_url ? (
              <img loading="lazy" decoding="async" src={group.avatar_url} alt="" className="h-10 w-10 shrink-0 rounded-xl object-cover" />
            ) : (
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-surface text-sm font-semibold">
                {group.name[0]?.toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold leading-tight">{group.name}</div>
              <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                <Users className="h-3 w-3" />
                {group.member_count} {group.member_count === 1 ? "membro" : "membros"} · #geral
              </div>
            </div>
          </Link>
        </div>

        {/* Messages */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto overscroll-contain px-3 py-4 sm:px-4"
        >
          {(msgQ.data ?? []).length === 0 ? (
            <div className="grid h-full place-items-center px-6 text-center">
              <div>
                <div className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-2xl bg-surface">
                  <Users className="h-6 w-6 text-muted-foreground" />
                </div>
                <p className="text-sm font-medium">Seja o primeiro a falar</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Cumprimente o grupo, compartilhe um disco ou uma novidade.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              {groups.map((g) => {
                if (g.kind === "day") {
                  return (
                    <div key={g.key} className="my-3 flex items-center justify-center">
                      <span className="rounded-full border border-[color:var(--hairline)] bg-surface/70 px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        {g.label}
                      </span>
                    </div>
                  );
                }
                const mine = g.userId === profile?.id;
                return (
                  <div
                    key={g.key}
                    className={cn("flex gap-2", mine ? "justify-end" : "justify-start")}
                  >
                    {!mine && (
                      <img loading="lazy" decoding="async"
                        src={g.author?.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${g.author?.username ?? "u"}`}
                        alt=""
                        className="mt-auto h-7 w-7 shrink-0 rounded-full"
                      />
                    )}
                    <div className={cn("flex max-w-[78%] flex-col gap-0.5 sm:max-w-[65%]", mine && "items-end")}>
                      {!mine && (
                        <div className="px-1 text-[11px] font-medium text-muted-foreground">
                          @{g.author?.username ?? "?"}
                        </div>
                      )}
                      {g.items.map((m, idx) => {
                        const isLast = idx === g.items.length - 1;
                        return (
                          <div
                            key={m.id}
                            className={cn(
                              "group/msg relative max-w-full px-3.5 py-2 text-sm leading-relaxed break-words whitespace-pre-wrap",
                              mine
                                ? "bg-primary text-primary-foreground"
                                : "bg-surface text-foreground",
                              // Rounded corners: pill first, tapered on the side, smoother when consecutive
                              "rounded-2xl",
                              mine
                                ? cn(
                                    idx === 0 ? "rounded-tr-2xl" : "rounded-tr-md",
                                    isLast ? "rounded-br-md" : "rounded-br-md",
                                  )
                                : cn(
                                    idx === 0 ? "rounded-tl-2xl" : "rounded-tl-md",
                                    isLast ? "rounded-bl-md" : "rounded-bl-md",
                                  ),
                            )}
                          >
                            <Linkify text={m.body} />
                            {isLast && (
                              <span
                                className={cn(
                                  "ml-2 inline-block align-baseline text-[10px] opacity-60",
                                  mine ? "text-primary-foreground" : "text-muted-foreground",
                                )}
                              >
                                {formatTime(m.created_at)}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Composer */}
        <form
          onSubmit={(e) => { e.preventDefault(); submit(); }}
          className="sticky bottom-0 z-10 flex items-end gap-2 border-t border-[color:var(--hairline)] bg-background/95 px-3 py-2.5 backdrop-blur supports-[backdrop-filter]:bg-background/80"
        >
          <div className="flex flex-1 items-end rounded-3xl border border-[color:var(--hairline)] bg-surface/60 px-4 py-2 focus-within:border-primary/60">
            <textarea
              ref={taRef}
              value={text}
              onChange={onInput}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  submit();
                }
              }}
              rows={1}
              placeholder="Mensagem..."
              className="max-h-[140px] w-full resize-none bg-transparent text-[15px] leading-6 outline-none placeholder:text-muted-foreground"
            />
          </div>
          <button
            type="submit"
            disabled={send.isPending || !text.trim()}
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground transition-opacity disabled:opacity-40"
            aria-label="Enviar"
          >
            <Send className="h-4 w-4" />
          </button>
        </form>
      </div>
    </AppShell>
  );
}
